import React from 'react';
import {Box, Button, Card, CardContent, CardHeader, Divider, MenuItem, TextField} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {useDispatch} from 'react-redux';
import logbookApi from '../../../../api/staffApi/logbookApi';
import dayjs from 'dayjs';
import {showNotification} from '../../../../redux/reducers/notificationReducer';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useSabitler} from '../../../../hooks/queries/useLogbookQueries';
import {useMutation, useQueryClient} from '@tanstack/react-query';

const validationSchema = Yup.object({
    miktar: Yup.number().required('Miktar zorunludur').positive('Miktar pozitif olmalıdır'),
    tip: Yup.number().required('Kategori seçimi zorunludur'),
    kaynak: Yup.number().required('Ödeme kaynağı zorunludur'),
    aciklama: Yup.string()
});

const HizliGiderEkle = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const {data: sabitler} = useSabitler();

    const mutation = useMutation({
        mutationFn: (payload: any) => logbookApi.postGider(payload),
        onSuccess: () => {
            dispatch(showNotification({
                message: "Gider başarıyla eklendi.",
                severity: "success"
            }));
            // Günsonu verisini cache'den temizle ki Personel listesi veya diğerleri güncellensin
            queryClient.invalidateQueries({queryKey: ['gunsonu']});
        }
    });

    const formik = useFormik({
        initialValues: {
            miktar: '',
            aciklama: '',
            tip: '',
            kaynak: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values, {resetForm}) => {
            const bugun = dayjs().format("DD-MM-YYYY");
            const payload = {
                tarih: bugun,
                miktar: parseFloat(values.miktar as string),
                aciklama: values.aciklama,
                tip: parseInt(values.tip as string),
                kaynak: parseInt(values.kaynak as string)
            };

            await mutation.mutateAsync(payload);
            resetForm();
        },
    });

    if (!sabitler) return null;

    const uygunHesaplar = sabitler.hesaplar.filter((h: any) =>
        ["İşletme", "Yönetici", "Kredi Kartı"].includes(h.hesapTipi) && h.aktif
    );

    return (
        <Card sx={{mb: 2}}>
            <CardHeader title="Hızlı Gider Ekle"/>
            <Divider/>
            <CardContent>
                <Box component="form" onSubmit={formik.handleSubmit}
                     sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Box sx={{display: 'flex', gap: 2}}>
                        <TextField
                            sx={{flex: 1}}
                            label="Miktar"
                            name="miktar"
                            type="number"
                            value={formik.values.miktar}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.miktar && Boolean(formik.errors.miktar)}
                            helperText={formik.touched.miktar && formik.errors.miktar}
                            required
                        />
                        <TextField
                            sx={{flex: 2}}
                            select
                            label="Kategori"
                            name="tip"
                            value={formik.values.tip}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tip && Boolean(formik.errors.tip)}
                            helperText={formik.touched.tip && formik.errors.tip}
                            required
                        >
                            {sabitler.gider_tipleri.map((tip: any) => (
                                <MenuItem key={tip.id} value={tip.id}>{tip.tip}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            sx={{flex: 2}}
                            select
                            label="Ödeme Kaynağı"
                            name="kaynak"
                            value={formik.values.kaynak}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.kaynak && Boolean(formik.errors.kaynak)}
                            helperText={formik.touched.kaynak && formik.errors.kaynak}
                            required
                        >
                            {uygunHesaplar.map((hesap: any) => (
                                <MenuItem key={hesap.id} value={hesap.id}>{hesap.hesapAdi}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                        <TextField
                            fullWidth
                            label="Açıklama"
                            name="aciklama"
                            value={formik.values.aciklama}
                            onChange={formik.handleChange}
                        />
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={formik.isSubmitting}
                            startIcon={<SaveIcon/>}
                        >
                            {formik.isSubmitting ? '...' : 'Kaydet'}
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default HizliGiderEkle;
